import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { db } from "../../firebase";
import { Modal, Form, Button, InputGroup } from "react-bootstrap";
import { MdModeEdit, MdRestoreFromTrash } from "react-icons/md";
import styles from "./detail.module.scss";
import {
  HEIGHT_OPTIONS,
  WINGSPAN_OPTIONS,
  STANDING_REACH_OPTIONS,
  HAND_L_OPTIONS,
  HAND_W_OPTIONS,
  VERT_MAX_OPTIONS,
  VERT_NO_STEP_OPTIONS,
} from "./measurementOptions";
import { formatWeightDisplay } from "../../utils/helpers";
import { parseDOB } from "../../utils/age";
import { formatMeasurementFeetInches } from "../../utils/feetInches";
import log_activity from "../activity/logger";
import { useAuth } from "../../Context/AuthContext";
import { notificationService } from "../../Services/notification-service";

export const MeasurementsSection = ({ playerID, playerName, playerDOB }) => {
  const { userInfo, getUserInfoData } = useAuth();
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingMeasurement, setEditingMeasurement] = useState(null);
  const [form, setForm] = useState({
    date: "",
    event: "",
    heightShoes: "",
    heightNone: "",
    heightNotSpecified: "",
    weight: "",
    wingspan: "",
    standingReach: "",
    vertMax: "",
    vertNoStep: "",
    bodyFat: "",
    handL: "",
    handW: "",
    laneAgility: "",
    shuttle: "",
    sprint: "",
  });

  // Age at event date from player DOB (read-only in form, stored on save)
  const calculatedAge = useMemo(() => {
    const dob = parseDOB(playerDOB);
    if (!dob || !form.date) return "";
    const eventDateStr = form.date.trim();
    if (!eventDateStr) return "";
    let eventDate;
    if (eventDateStr.includes("/")) {
      const parts = eventDateStr.split("/");
      const mm = parseInt(parts[0], 10);
      const dd = parseInt(parts[1], 10);
      const yy = parseInt(parts[2], 10);
      const y = parts[2].length === 2 ? 2000 + yy : yy;
      eventDate = new Date(y, mm - 1, dd);
    } else {
      eventDate = new Date(eventDateStr);
    }
    if (isNaN(eventDate.getTime())) return "";
    const ageMs = eventDate - dob;
    const ageYears = ageMs / (365.25 * 24 * 60 * 60 * 1000);
    return ageYears >= 0 ? ageYears.toFixed(1) : "";
  }, [playerDOB, form.date]);

  // Shared loader so we can refresh after add/edit/delete
  const loadMeasurements = async () => {
    if (!playerID) return;
    setLoading(true);
    try {
      const snapshot = await db
        .collection("player-measurements")
        .where("playerProfile", "==", playerID)
        .get();

      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort newest date first
      data.sort((a, b) => {
        const da = a.date ? new Date(a.date) : 0;
        const dbDate = b.date ? new Date(b.date) : 0;
        return dbDate - da;
      });

      setMeasurements(data);
    } catch (error) {
      console.error("Error fetching measurements:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeasurements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerID]);

  // Format YYYY-MM-DD (or Date) → MM/DD/YY for display & storage
  const formatToMMDDYY = (val) => {
    if (!val) return "";
    const d = typeof val === "string" ? new Date(val) : val;
    if (isNaN(d.getTime())) return val;
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yy = String(d.getFullYear()).slice(-2);
    return `${mm}/${dd}/${yy}`;
  };

  // Format MM/DD/YY (or YYYY-MM-DD) → YYYY-MM-DD for date input
  const formatToYYYYMMDD = (val) => {
    if (!val) return "";
    if (val.includes("/")) {
      const [mm, dd, yy] = val.split("/");
      const year = yy.length === 2 ? 2000 + parseInt(yy, 10) : parseInt(yy, 10);
      return `${year}-${mm}-${dd}`;
    }
    return val;
  };

  const emptyForm = {
    date: "",
    event: "",
    heightShoes: "",
    heightNone: "",
    heightNotSpecified: "",
    weight: "",
    wingspan: "",
    standingReach: "",
    vertMax: "",
    vertNoStep: "",
    bodyFat: "",
    handL: "",
    handW: "",
    laneAgility: "",
    shuttle: "",
    sprint: "",
  };

  const resetFormAndClose = () => {
    setForm(emptyForm);
    setEditingMeasurement(null);
    setShowModal(false);
  };

  const openAddModal = () => {
    setForm(emptyForm);
    setEditingMeasurement(null);
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!playerID) return;

    try {
      const eventDateStr = formatToMMDDYY(form.date) || form.date;
      const payload = {
        ...form,
        date: eventDateStr,
        age: calculatedAge !== "" ? calculatedAge : "",
        playerProfile: playerID,
        playerName: playerName || "",
        ...(editingMeasurement
          ? { updatedAt: new Date() }
          : { createdAt: new Date() }),
      };

      if (editingMeasurement && editingMeasurement.id) {
        await db
          .collection("player-measurements")
          .doc(editingMeasurement.id)
          .update(payload);
        toast.success("Measurement updated successfully");
      } else {
        await db.collection("player-measurements").add(payload);
        toast.success("Measurement added successfully");
      }

      const editorName = (userInfo || getUserInfoData()) ? `${(userInfo || getUserInfoData()).firstName} ${(userInfo || getUserInfoData()).lastName}` : "Unknown User";
      try {
        log_activity(
          playerName || "Player",
          editingMeasurement ? "Measurement Edited" : "New Measurement Added",
          editorName
        );
      } catch (logErr) {
        console.error("Error logging measurement activity:", logErr);
      }

      try {
        const watchlistSnap = await db.collection("watchlist").doc(playerID).get();
        if (watchlistSnap.exists) {
          const subscribers = watchlistSnap.data().subscribers || [];
          const displayName = (playerName && playerName.trim()) ? playerName.trim() : "Player";
          notificationService.send(
            subscribers,
            `${displayName}'s profile has been updated.`,
            playerID
          );
        }
      } catch (notifErr) {
        console.error("Error sending watchlist notification:", notifErr);
      }

      await loadMeasurements();
      resetFormAndClose();
    } catch (error) {
      console.error("Error saving measurement:", error);
      toast.error("Error saving measurement");
    }
  };

  const handleEdit = (measurement) => {
    setEditingMeasurement(measurement);
    const weightVal = measurement.weight != null && measurement.weight !== ""
      ? String(measurement.weight).replace(/\s*lbs\.?\s*$/i, "").trim()
      : "";
    setForm({
      date: formatToYYYYMMDD(measurement.date) || measurement.date || "",
      event: measurement.event ?? measurement.source ?? "",
      heightShoes: measurement.heightShoes || "",
      heightNone: measurement.heightNone || "",
      heightNotSpecified: measurement.heightNotSpecified || "",
      weight: weightVal,
      wingspan: measurement.wingspan || "",
      standingReach: measurement.standingReach || "",
      vertMax: measurement.vertMax || "",
      vertNoStep: measurement.vertNoStep || "",
      bodyFat: measurement.bodyFat || "",
      handL: measurement.handL || "",
      handW: measurement.handW || "",
      laneAgility: measurement.laneAgility ?? measurement.handLA ?? "",
      shuttle: measurement.shuttle ?? measurement.shuffle ?? "",
      sprint: measurement.sprint || "",
    });
    setShowModal(true);
  };

  const handleDelete = async (measurement) => {
    if (!window.confirm("Are you sure you want to delete this measurement?")) {
      return;
    }

    try {
      await db.collection("player-measurements").doc(measurement.id).delete();
      await loadMeasurements();
      toast.success("Measurement deleted successfully");
    } catch (error) {
      console.error("Error deleting measurement:", error);
      toast.error("Error deleting measurement");
    }
  };

  return (
    <>
      <div className={styles.feed}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2>Measurements / Testing</h2>
          </div>
          <div className={styles.headerRight}>
            <button
              type="button"
              className={`btn btn-dark ${styles.addMeasurementButton}`}
              onClick={openAddModal}
            >
              Add Measurements
            </button>
          </div>
        </div>

        {loading ? (
          <div className={styles.measurementsEmptyState}>
            <p>Loading measurements...</p>
          </div>
        ) : measurements.length === 0 ? (
          <div className={styles.measurementsEmptyState}>
            <p>No measurements added yet.</p>
          </div>
        ) : (
          (() => {
            const showHeightNotSpecified = measurements.some(
              (m) => m.heightNotSpecified != null && String(m.heightNotSpecified).trim() !== ""
            );
            return (
              <div className={styles.tableContainer}>
                <table className={styles.measurementsTable}>
                  <thead>
                    <tr>
                      <th rowSpan="2">Event Date</th>
                      <th rowSpan="2">Event</th>
                      <th rowSpan="2">Age</th>
                      <th colSpan={showHeightNotSpecified ? 3 : 2}>Height</th>
                      <th rowSpan="2">Weight</th>
                      <th rowSpan="2">Wingspan</th>
                      <th rowSpan="2" className={styles.tableHeaderTwoLine}>Standing<br />Reach</th>
                      <th colSpan="2">Vertical Jump</th>
                      <th rowSpan="2" className={styles.tableHeaderTwoLine}>Body<br />Fat %</th>
                      <th colSpan="2">Hand</th>
                      <th rowSpan="2" className={styles.tableHeaderTwoLine}>Lane<br />Agility (sec.)</th>
                      <th rowSpan="2" className={styles.tableHeaderTwoLine}>Shuttle<br />(sec.)</th>
                      <th rowSpan="2" className={styles.tableHeaderTwoLine}>Sprint<br />(sec.)</th>
                      <th rowSpan="2">Action</th>
                    </tr>
                    <tr>
                      <th>w/ shoes</th>
                      <th>w/o shoes</th>
                      {showHeightNotSpecified && (
                        <th className={styles.tableHeaderTwoLine}>w/ or w/o shoes not specified*</th>
                      )}
                      <th>Max</th>
                      <th>No Step</th>
                      <th>Length</th>
                      <th>Width</th>
                    </tr>
                  </thead>
                  <tbody>
                    {measurements.map((m) => (
                      <tr key={m.id}>
                        <td>{m.date ? (m.date.includes("/") ? m.date : formatToMMDDYY(m.date)) : "--"}</td>
                        <td>{(m.event ?? m.source) || "--"}</td>
                        <td>{m.age || "--"}</td>
                        <td>{formatMeasurementFeetInches(m.heightShoes)}</td>
                        <td>{formatMeasurementFeetInches(m.heightNone)}</td>
                        {showHeightNotSpecified && (
                          <td>{formatMeasurementFeetInches(m.heightNotSpecified)}</td>
                        )}
                        <td>{formatWeightDisplay(m.weight)}</td>
                        <td>{formatMeasurementFeetInches(m.wingspan)}</td>
                        <td>{formatMeasurementFeetInches(m.standingReach)}</td>
                        <td>{m.vertMax || "--"}</td>
                        <td>{m.vertNoStep || "--"}</td>
                        <td>{m.bodyFat != null && m.bodyFat !== "" ? (String(m.bodyFat).includes("%") ? m.bodyFat : `${m.bodyFat}%`) : "--"}</td>
                        <td>{m.handL || "--"}</td>
                        <td>{m.handW || "--"}</td>
                        <td>{(m.laneAgility ?? m.handLA) || "--"}</td>
                        <td>{(m.shuttle ?? m.shuffle) || "--"}</td>
                        <td>{m.sprint || "--"}</td>
                        <td>
                          <div className={styles.actionIcon}>
                            <MdModeEdit
                              onClick={() => handleEdit(m)}
                              style={{ color: "green" }}
                            />
                            <MdRestoreFromTrash
                              onClick={() => handleDelete(m)}
                              style={{ color: "red" }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()
        )}
      </div>

      <Modal show={showModal} onHide={resetFormAndClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingMeasurement ? "Edit Measurement" : "Add Measurements"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className={styles.measurementsModalBody}>
            {/* Event Date & Event — full row by itself */}
            <div className={styles.measurementsFormSection}>
              <div className={styles.measurementsFormSectionTitle}>Event Date &amp; Event</div>
              <div className={styles.measurementsFormRow}>
                <Form.Group className={`mb-3 ${styles.measurementsFormField}`}>
                  <Form.Label>Event Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="date"
                    value={form.date}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
                <Form.Group className={`mb-3 ${styles.measurementsFormField}`}>
                  <Form.Label>Event</Form.Label>
                  <Form.Control
                    type="text"
                    name="event"
                    value={form.event}
                    onChange={handleChange}
                    placeholder="ex. 2026 Nike Hoop Summit"
                  />
                </Form.Group>
                <Form.Group className={`mb-3 ${styles.measurementsFormField}`}>
                  <Form.Label>Age</Form.Label>
                  <Form.Control
                    type="text"
                    value={calculatedAge}
                    readOnly
                    disabled
                    className="bg-light"
                  />
                </Form.Group>
              </div>
            </div>

            {/* Remaining sections in two columns */}
            <div className={styles.measurementsFormGrid}>
              {/* Left column */}
              <div className={styles.measurementsFormColumn}>
                <div className={styles.measurementsFormSection}>
                  <div className={styles.measurementsFormSectionTitle}>Height</div>
                  <div className={styles.measurementsFormRow}>
                    <Form.Group className={`mb-3 ${styles.measurementsFormField}`}>
                      <Form.Label>w/ shoes</Form.Label>
                      <Form.Select
                        name="heightShoes"
                        value={form.heightShoes}
                        onChange={handleChange}
                      >
                        {HEIGHT_OPTIONS.map((opt) => (
                          <option key={opt.value || "empty"} value={opt.value}>{opt.label || ""}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className={`mb-3 ${styles.measurementsFormField}`}>
                      <Form.Label>w/o shoes</Form.Label>
                      <Form.Select
                        name="heightNone"
                        value={form.heightNone}
                        onChange={handleChange}
                      >
                        {HEIGHT_OPTIONS.map((opt) => (
                          <option key={opt.value || "empty"} value={opt.value}>{opt.label || ""}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className={`mb-3 ${styles.measurementsFormField}`}>
                      <Form.Label>w/ or w/o shoes not specified*</Form.Label>
                      <Form.Select
                        name="heightNotSpecified"
                        value={form.heightNotSpecified}
                        onChange={handleChange}
                      >
                        {HEIGHT_OPTIONS.map((opt) => (
                          <option key={opt.value || "empty"} value={opt.value}>{opt.label || ""}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </div>
                <div className={styles.measurementsFormSection}>
                  <div className={styles.measurementsFormSectionTitle}>Weight, Wingspan &amp; Reach</div>
                  <div className={styles.measurementsFormRow}>
                    <Form.Group className={`mb-3 ${styles.measurementsFormField}`}>
                      <Form.Label>Weight</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          name="weight"
                          value={form.weight}
                          onChange={handleChange}
                          step="0.1"
                          min="100"
                          max="400"
                          placeholder="e.g. 185.5"
                        />
                        <InputGroup.Text>lbs.</InputGroup.Text>
                      </InputGroup>
                    </Form.Group>
                    <Form.Group className={`mb-3 ${styles.measurementsFormField}`}>
                      <Form.Label>Wingspan</Form.Label>
                      <Form.Select name="wingspan" value={form.wingspan} onChange={handleChange}>
                        {WINGSPAN_OPTIONS.map((opt) => (
                          <option key={opt.value || "empty"} value={opt.value}>{opt.label || ""}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className={`mb-3 ${styles.measurementsFormField}`}>
                      <Form.Label>Standing Reach</Form.Label>
                      <Form.Select name="standingReach" value={form.standingReach} onChange={handleChange}>
                        {STANDING_REACH_OPTIONS.map((opt) => (
                          <option key={opt.value || "empty"} value={opt.value}>{opt.label || ""}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </div>
                <div className={styles.measurementsFormSection}>
                  <div className={styles.measurementsFormSectionTitle}>Vertical Jump</div>
                  <div className={styles.measurementsFormRow}>
                    <Form.Group className={`mb-3 ${styles.measurementsFormField}`}>
                      <Form.Label>Max</Form.Label>
                      <Form.Select name="vertMax" value={form.vertMax} onChange={handleChange}>
                        {VERT_MAX_OPTIONS.map((opt) => (
                          <option key={opt.value || "empty"} value={opt.value}>{opt.label || ""}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className={`mb-3 ${styles.measurementsFormField}`}>
                      <Form.Label>No Step</Form.Label>
                      <Form.Select name="vertNoStep" value={form.vertNoStep} onChange={handleChange}>
                        {VERT_NO_STEP_OPTIONS.map((opt) => (
                          <option key={opt.value || "empty"} value={opt.value}>{opt.label || ""}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <div className={styles.measurementsFormField} />
                  </div>
                </div>
              </div>
              {/* Right column */}
              <div className={styles.measurementsFormColumn}>
                <div className={styles.measurementsFormSection}>
                  <div className={styles.measurementsFormSectionTitle}>Body Fat %</div>
                  <div className={styles.measurementsFormRow}>
                    <Form.Group className={`mb-3 ${styles.measurementsFormField}`}>
                      <Form.Label>Body Fat %</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          name="bodyFat"
                          value={form.bodyFat}
                          onChange={handleChange}
                          step="0.25"
                          min="0"
                          max="50"
                          placeholder="e.g. 12.5"
                        />
                        <InputGroup.Text>%</InputGroup.Text>
                      </InputGroup>
                    </Form.Group>
                    <div className={styles.measurementsFormField} />
                    <div className={styles.measurementsFormField} />
                  </div>
                </div>
                <div className={styles.measurementsFormSection}>
                  <div className={styles.measurementsFormSectionTitle}>Hands (Length &amp; Width)</div>
                  <div className={styles.measurementsFormRow}>
                    <Form.Group className={`mb-3 ${styles.measurementsFormField}`}>
                      <Form.Label>Length</Form.Label>
                      <Form.Select name="handL" value={form.handL} onChange={handleChange}>
                        {HAND_L_OPTIONS.map((opt) => (
                          <option key={opt.value || "empty"} value={opt.value}>{opt.label || ""}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className={`mb-3 ${styles.measurementsFormField}`}>
                      <Form.Label>Width</Form.Label>
                      <Form.Select name="handW" value={form.handW} onChange={handleChange}>
                        {HAND_W_OPTIONS.map((opt) => (
                          <option key={opt.value || "empty"} value={opt.value}>{opt.label || ""}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <div className={styles.measurementsFormField} />
                  </div>
                </div>
                <div className={styles.measurementsFormSection}>
                  <div className={styles.measurementsFormSectionTitle}>Shuttle, Sprint &amp; Lane Agility (sec.)</div>
                  <div className={styles.measurementsFormRow}>
                    <Form.Group className={`mb-3 ${styles.measurementsFormField}`}>
                      <Form.Label>Shuttle</Form.Label>
                      <Form.Control
                        type="number"
                        name="shuttle"
                        value={form.shuttle}
                        onChange={handleChange}
                        step="0.001"
                        min="2"
                        max="15"
                        placeholder="e.g. 8.32"
                      />
                    </Form.Group>
                    <Form.Group className={`mb-3 ${styles.measurementsFormField}`}>
                      <Form.Label>Sprint</Form.Label>
                      <Form.Control
                        type="number"
                        name="sprint"
                        value={form.sprint}
                        onChange={handleChange}
                        step="0.001"
                        min="2.5"
                        max="6"
                        placeholder="e.g. 3.68"
                      />
                    </Form.Group>
                    <Form.Group className={`mb-3 ${styles.measurementsFormField}`}>
                      <Form.Label>Lane Agility</Form.Label>
                      <Form.Control
                        type="number"
                        name="laneAgility"
                        value={form.laneAgility}
                        onChange={handleChange}
                        step="0.01"
                        min="8"
                        max="20"
                        placeholder="e.g. 11.25"
                      />
                    </Form.Group>
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={resetFormAndClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Save
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

      